import React, { useState } from "react";
import { TrendingUp, Headset, MessageCircle } from "lucide-react";

export default function FranchiseSection({ franchise }) {
  const f = franchise || {};
  const bgColor = f.bgColor || "#1f3a2c";
  const buttonColor = f.buttonColor || "#25D366";
  const ph = f.placeholders || {};

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    city: "",
    state: "",
    budget: "",
    info: "",
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const msg = `*Franchise Inquiry*
*Name:* ${form.name}
*Email:* ${form.email}
*Mobile:* ${form.mobile}
*WhatsApp:* ${form.whatsapp}
*City:* ${form.city}
*State:* ${form.state}
*Budget:* ${form.budget}
*Info:* ${form.info}`;
    const url = `https://wa.me/${f.whatsappNumber}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  return (
    <section
      id="franchise"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ backgroundColor: bgColor }}
    >
      {/* Decorative radial */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 30% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6 text-white">
            <p className="text-[12px] tracking-tight-wide text-[#c4a574] font-semibold">
              {f.eyebrow}
            </p>
            <h2 className="font-serif-display text-5xl md:text-6xl text-white">
              {f.title}
            </h2>
            <h3 className="font-serif-display text-2xl md:text-3xl text-white/85">
              {f.subtitle}
            </h3>
            <div
              className="text-[15px] leading-[1.9] text-white/75 rich-text max-w-xl"
              dangerouslySetInnerHTML={{ __html: f.description || "" }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
              {(f.bullets || []).map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    {i === 0 ? (
                      <TrendingUp className="w-5 h-5 text-[#c4a574]" />
                    ) : (
                      <Headset className="w-5 h-5 text-[#c4a574]" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-serif-display text-xl text-white">
                      {b.title}
                    </h4>
                    <p className="text-sm text-white/65">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={submit}
            className="bg-[#faf3e3] p-7 md:p-9 rounded-2xl shadow-2xl space-y-4"
          >
            <h4 className="font-serif-display text-2xl text-[#2a2a2a] mb-2">
              Franchise Application
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                placeholder={ph.name || "John Doe"}
                value={form.name}
                onChange={(v) => update("name", v)}
                required
              />
              <Input
                label="Email"
                type="email"
                placeholder={ph.email || "john@example.com"}
                value={form.email}
                onChange={(v) => update("email", v)}
                required
              />
              <Input
                label="Mobile Number"
                placeholder={ph.mobile || "+91 98765 43210"}
                value={form.mobile}
                onChange={(v) => update("mobile", v)}
                required
              />
              <Input
                label="WhatsApp Number"
                placeholder={ph.whatsapp || "+91 98765 43210"}
                value={form.whatsapp}
                onChange={(v) => update("whatsapp", v)}
              />
              <Input
                label="City"
                placeholder={ph.city || "Ahmedabad"}
                value={form.city}
                onChange={(v) => update("city", v)}
              />
              <Input
                label="State"
                placeholder={ph.state || "Gujarat"}
                value={form.state}
                onChange={(v) => update("state", v)}
              />
            </div>
            <div>
              <label className="block text-[11px] tracking-tight-wide text-[#8a7a5c] font-semibold mb-1">
                INVESTMENT BUDGET
              </label>
              <select
                value={form.budget}
                onChange={(e) => update("budget", e.target.value)}
                className="w-full bg-white border border-[#e6d6b6] rounded-md py-2.5 px-3 text-sm text-[#2a2a2a] focus:outline-none focus:border-[#a08862]"
              >
                <option value="">Select your budget range</option>
                {(f.budgetOptions || []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] tracking-tight-wide text-[#8a7a5c] font-semibold mb-1">
                ADDITIONAL INFORMATION
              </label>
              <textarea
                value={form.info}
                onChange={(e) => update("info", e.target.value)}
                placeholder={ph.info || "Tell us about your experience and goals..."}
                rows={3}
                className="w-full bg-white border border-[#e6d6b6] rounded-md py-2.5 px-3 text-sm text-[#2a2a2a] focus:outline-none focus:border-[#a08862] resize-none"
              />
            </div>
            <button
              type="submit"
              style={{ backgroundColor: buttonColor }}
              className="mt-2 w-full hover:opacity-90 text-white text-[12px] tracking-tight-wide py-4 rounded-md transition-opacity duration-300 inline-flex items-center justify-center gap-2 font-semibold"
            >
              <MessageCircle size={16} fill="white" /> SEND VIA WHATSAPP
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Input({ label, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] tracking-tight-wide text-[#8a7a5c] font-semibold mb-1">
        {label.toUpperCase()}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full bg-white border border-[#e6d6b6] rounded-md py-2.5 px-3 text-sm text-[#2a2a2a] placeholder:text-[#b8a888] focus:outline-none focus:border-[#a08862]"
      />
    </div>
  );
}
